export type MarketingKeywordTopic = {
  slug: string;
  day: string;
  keyword: string;
  audience: string;
  title: string;
  landingPrompt: string;
  summary: string;
  proofPoints: string[];
  articleSections: Array<{
    title: string;
    body: string;
  }>;
};

export const marketingKeywordTopics: MarketingKeywordTopic[] = [
  {
    slug: 'career-roadmap-software',
    day: 'Monday',
    keyword: 'career roadmap software',
    audience: 'career starters and role switchers',
    title: 'Turn a target role into a practical career roadmap',
    landingPrompt: 'A role goal should become a visible path, not a vague list of advice.',
    summary:
      'CareerTree turns one target role into a focused roadmap with core nodes, study gates, and one small action that can become proof.',
    proofPoints: [
      'Generated role tree for a target job',
      'Core nodes organized into a first learning path',
      'A Day 1 task that starts the proof loop',
    ],
    articleSections: [
      {
        title: 'Why a roadmap matters',
        body:
          'Most career planning starts too wide. CareerTree keeps the first step concrete by turning one target role into a small sequence of learning nodes and actions.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'The product starts with a role, creates a visible tree, and guides the learner through study, quiz, mini action, growth log, and Win story.',
      },
    ],
  },
  {
    slug: 'portfolio-evidence-builder',
    day: 'Tuesday',
    keyword: 'portfolio evidence builder',
    audience: 'learners preparing proof for interviews',
    title: 'Build portfolio evidence from small daily actions',
    landingPrompt: 'A portfolio is stronger when each claim points to a saved piece of work.',
    summary:
      'CareerTree helps learners turn small actions into reusable evidence for portfolios, interviews, and public Win stories.',
    proofPoints: [
      'Mini actions attached to learning nodes',
      'Growth logs with artifact and reasoning',
      'Masked public Win posts for modest proof sharing',
    ],
    articleSections: [
      {
        title: 'Why evidence beats claims',
        body:
          'A skill claim is easy to ignore. A small artifact with context, reasoning, and reflection gives recruiters or mentors something concrete to inspect.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'CareerTree connects each learning node to a mini action and saves the result as proof, so the learner can reuse it beyond the app.',
      },
    ],
  },
  {
    slug: 'developer-career-path',
    day: 'Wednesday',
    keyword: 'developer career path',
    audience: 'developers building technical proof',
    title: 'Practice technical judgment, not just quiz memory',
    landingPrompt: 'Developer paths need proof of reasoning: tradeoffs, edge cases, and verification habits.',
    summary:
      'CareerTree developer paths add code reasoning prompts so technical learners can explain decisions before saving proof.',
    proofPoints: [
      'Technical tracks for Backend, Data, DevOps/SRE, AI, and Frontend',
      'Code Challenge prompts after quiz and mini action',
      'Rubric feedback on implementation judgment',
    ],
    articleSections: [
      {
        title: 'Why developer proof is different',
        body:
          'For technical roles, the useful signal is not only what someone knows. It is how they choose an approach, name the tradeoff, and check the result.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'Technical paths can include a Code Challenge that asks for implementation reasoning, edge cases, and a verification plan.',
      },
    ],
  },
  {
    slug: 'interview-proof-stories',
    day: 'Thursday',
    keyword: 'interview proof stories',
    audience: 'job seekers preparing interview examples',
    title: 'Turn learning work into interview-ready stories',
    landingPrompt: 'Interview prep gets easier when today\'s work becomes a clear before, action, and result story.',
    summary:
      'CareerTree keeps proof small and repeatable so a learner can turn progress into interview stories without inflating claims.',
    proofPoints: [
      'Before, action, and after story structure',
      'Win board examples labeled clearly',
      'No hiring guarantee or inflated outcome claims',
    ],
    articleSections: [
      {
        title: 'Why stories need proof',
        body:
          'Interview answers are stronger when they point to something the learner actually did. CareerTree keeps the story grounded in modest, reviewable work.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'The product turns a task into a saved growth log and can help shape that log into a public or private Win story.',
      },
    ],
  },
  {
    slug: 'skill-growth-log',
    day: 'Friday',
    keyword: 'skill growth log',
    audience: 'learners tracking visible progress',
    title: 'Track skill growth with proof, not streaks alone',
    landingPrompt: 'Progress tracking should show what changed, what was built, and what can be reused.',
    summary:
      'CareerTree combines learning progress with proof logs so each step can become portfolio or interview material.',
    proofPoints: [
      'Growth dashboard for saved work',
      'Proof attached to the learning loop',
      'Progress and Win signals in one product',
    ],
    articleSections: [
      {
        title: 'Why logs need context',
        body:
          'A streak says someone showed up. A growth log shows what they practiced, what they learned, and what evidence they can reuse.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'CareerTree connects study, quiz, mini action, proof, and Win stories so progress is tied to visible work.',
      },
    ],
  },
  {
    slug: 'ai-career-planning-tool',
    day: 'Saturday',
    keyword: 'AI career planning tool',
    audience: 'people who need a concrete starting point',
    title: 'Use AI to make the first career step smaller',
    landingPrompt: 'AI is useful here only when it turns a role goal into a concrete first action.',
    summary:
      'CareerTree uses AI to draft role paths, study cards, quiz gates, and mini actions that move learners toward visible proof.',
    proofPoints: [
      'AI-generated learning roadmaps',
      'Focused cards and short quiz gates',
      'Mini actions designed to become evidence',
    ],
    articleSections: [
      {
        title: 'Why AI needs a workflow',
        body:
          'A generic AI answer can feel helpful but disappear quickly. CareerTree wraps AI output in a repeatable product loop.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'The product uses AI inside a structured path: role, tree, study, quiz, action, growth log, and Win.',
      },
    ],
  },
  {
    slug: 'job-search-portfolio-tool',
    day: 'Sunday',
    keyword: 'job search portfolio tool',
    audience: 'job seekers turning preparation into assets',
    title: 'Make job search preparation reusable',
    landingPrompt: 'The best job-search work can become proof for a profile, portfolio, or interview answer.',
    summary:
      'CareerTree helps learners convert preparation into reusable assets instead of leaving notes scattered across tools.',
    proofPoints: [
      'Role-specific learning paths',
      'Saved proof from daily actions',
      'Success Board posts for approved progress stories',
    ],
    articleSections: [
      {
        title: 'Why preparation should become an asset',
        body:
          'Job search preparation often gets lost in notes, tabs, and practice files. CareerTree gives that work a place to become proof.',
      },
      {
        title: 'How CareerTree handles it',
        body:
          'A learner can start from one role, finish a small proof loop, and save the result for future portfolio or interview use.',
      },
    ],
  },
];

export function getDailyMarketingTopic(date = new Date()) {
  return marketingKeywordTopics[date.getDay() === 0 ? 6 : date.getDay() - 1];
}

export function getMarketingTopicBySlug(slug: string | undefined) {
  return marketingKeywordTopics.find((topic) => topic.slug === slug);
}
